import parsePhoneNumber from "libphonenumber-js";
import {
  PASSWORD_POLICY_MESSAGE,
  isPasswordPolicyInvalid,
} from "../authFieldPolicy";

export { PASSWORD_POLICY_MESSAGE };

const NAME_CHAR_PATTERN = /^[\p{L}\s'.-]+$/u;

export const NAME_VALIDATION_MESSAGES = {
  firstNameRequired: "First name is required.",
  firstNameLength: "First name must be between 1 and 50 characters.",
  firstNameChars:
    "First name may only contain letters, spaces, apostrophes, hyphens, and periods.",
  lastNameRequired: "Last name is required.",
  lastNameLength: "Last name must be between 1 and 75 characters.",
  lastNameChars:
    "Last name may only contain letters, spaces, apostrophes, hyphens, and periods.",
  phoneRequired: "Phone number is required.",
  phoneInvalid: "Enter a valid phone number.",
  passwordRequired: "Password is required.",
  passwordPolicy: PASSWORD_POLICY_MESSAGE,
  confirmPasswordRequired: "Please confirm your password.",
  confirmPasswordMismatch: "Password and confirm password do not match.",
  acceptedTermsRequired:
    "You must accept the SMS consent to create an account.",
};

export function isValidNameCharacters(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  return NAME_CHAR_PATTERN.test(trimmed);
}

export function validateFirstName(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return NAME_VALIDATION_MESSAGES.firstNameRequired;
  if (trimmed.length > 50) return NAME_VALIDATION_MESSAGES.firstNameLength;
  if (!isValidNameCharacters(trimmed)) {
    return NAME_VALIDATION_MESSAGES.firstNameChars;
  }
  return null;
}

export function validateLastName(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return NAME_VALIDATION_MESSAGES.lastNameRequired;
  if (trimmed.length > 75) return NAME_VALIDATION_MESSAGES.lastNameLength;
  if (!isValidNameCharacters(trimmed)) {
    return NAME_VALIDATION_MESSAGES.lastNameChars;
  }
  return null;
}

export function formatPhoneNumberE164(rawPhone) {
  const trimmed = String(rawPhone ?? "").trim();
  if (!trimmed) return "";
  const normalized = trimmed.startsWith("+")
    ? trimmed
    : `+${trimmed.replace(/\D/g, "")}`;
  try {
    const parsed = parsePhoneNumber(normalized);
    if (parsed?.isValid()) {
      return parsed.format("E.164");
    }
  } catch {
    return "";
  }
  return "";
}

export function validatePhoneNumber(rawPhone) {
  const trimmed = String(rawPhone ?? "").trim();
  if (!trimmed) return NAME_VALIDATION_MESSAGES.phoneRequired;
  const e164 = formatPhoneNumberE164(trimmed);
  if (!e164) return NAME_VALIDATION_MESSAGES.phoneInvalid;
  return null;
}

export function validatePassword(value) {
  if (!value) return NAME_VALIDATION_MESSAGES.passwordRequired;
  if (isPasswordPolicyInvalid(value)) {
    return NAME_VALIDATION_MESSAGES.passwordPolicy;
  }
  return null;
}

export function validateConfirmPassword(password, confirmPassword) {
  if (!confirmPassword) return NAME_VALIDATION_MESSAGES.confirmPasswordRequired;
  if (password !== confirmPassword) {
    return NAME_VALIDATION_MESSAGES.confirmPasswordMismatch;
  }
  return null;
}

export function validateAcceptedTerms(value) {
  if (!value) return NAME_VALIDATION_MESSAGES.acceptedTermsRequired;
  return null;
}

export function validateSignupForm(values) {
  const errors = {};
  const firstNameError = validateFirstName(values.firstName);
  const lastNameError = validateLastName(values.lastName);
  const phoneError = validatePhoneNumber(values.phoneNumber);
  const passwordError = validatePassword(values.password);
  const confirmPasswordError = validateConfirmPassword(
    values.password,
    values.confirmPassword
  );
  const acceptedTermsError = validateAcceptedTerms(values.acceptedTerms);

  if (firstNameError) errors.firstName = firstNameError;
  if (lastNameError) errors.lastName = lastNameError;
  if (phoneError) errors.phoneNumber = phoneError;
  if (passwordError) errors.password = passwordError;
  if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;
  if (acceptedTermsError) errors.acceptedTerms = acceptedTermsError;

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}

export function parseInviteQueryParams(search = window.location.search) {
  const params = new URLSearchParams(search);
  const inviteToken = String(params.get("token") ?? "").trim();
  const queryError = inviteToken ? null : "missing_token";

  return {
    inviteToken,
    isValid: !queryError,
    queryError,
  };
}

export function buildInviteAcceptancePayload(invite, formValues) {
  const phoneNumber = formatPhoneNumberE164(formValues.phoneNumber);
  return {
    inviteToken: invite.inviteToken,
    email: invite.email,
    firstName: String(formValues.firstName ?? "").trim(),
    lastName: String(formValues.lastName ?? "").trim(),
    password: formValues.password,
    phoneNumber,
    acceptedTerms: Boolean(formValues.acceptedTerms),
  };
}
